import { useEffect, useMemo, useRef, useState } from 'react'
import { Upload, FileSpreadsheet, AlertTriangle, X } from 'lucide-react'
import { Modal, Button, Select, DateInput } from '@/components/ui'
import { useRotaUploadPreview, useRotaUploadCommit } from '../hooks/useShifts'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { cn } from '@/lib/cn'
import type { MeCompany } from '@/types/company.types'
import type {
  RotaCellIssue,
  RotaConflictPolicy,
  RotaPreviewResult,
  RotaValidationErrorBody,
} from '@/types/shift.types'
import { describeEligibilityReasons } from '../utils/shiftEligibility'

interface UploadRotaModalProps {
  companies: MeCompany[]
  defaultCompanyId?: string
  open: boolean
  onClose: () => void
  /** Default range pre-filled from the page's current view. YYYY-MM-DD. */
  startDate: string
  endDate: string
}

const XLSX_EXT = '.xlsx'
const MAX_BYTES = 10 * 1024 * 1024
/** Path of the downloadable template asset served from /public. */
const TEMPLATE_HREF = '/templates/rota-template.xlsx'

/** Pulls per-cell issues off a 422 ROTA validation error, if present. */
function extractRotaIssues(error: unknown): RotaCellIssue[] | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | Partial<RotaValidationErrorBody>
    | undefined
  if (data && data.code === 'ROTA_VALIDATION_FAILED' && Array.isArray(data.issues)) {
    return data.issues
  }
  return null
}

export function UploadRotaModal({
  companies,
  defaultCompanyId,
  open,
  onClose,
  startDate: defaultStart,
  endDate: defaultEnd,
}: UploadRotaModalProps): React.ReactElement {
  const lockedCompany = companies.length === 1
  const [companyId, setCompanyId] = useState(
    defaultCompanyId ?? (lockedCompany ? companies[0]?._id : '') ?? ''
  )
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<RotaPreviewResult | null>(null)
  const [issues, setIssues] = useState<RotaCellIssue[]>([])
  const [confirmAck, setConfirmAck] = useState(false)
  const [conflictPolicy, setConflictPolicy] = useState<RotaConflictPolicy>('replace')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const previewMutation = useRotaUploadPreview()
  const commitMutation = useRotaUploadCommit()

  // Reset transient state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setCompanyId(defaultCompanyId ?? (lockedCompany ? companies[0]?._id : '') ?? '')
      setStartDate(defaultStart)
      setEndDate(defaultEnd)
      setFile(null)
      setFileError(null)
      setPreview(null)
      setIssues([])
      setConfirmAck(false)
      setConflictPolicy('replace')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c._id, label: c.name })),
    [companies]
  )

  // Any change to inputs invalidates a stale preview.
  function invalidatePreview(): void {
    setPreview(null)
    setIssues([])
    setConfirmAck(false)
  }

  function acceptFile(next: File | null): void {
    invalidatePreview()
    if (!next) {
      setFile(null)
      return
    }
    if (!next.name.toLowerCase().endsWith(XLSX_EXT)) {
      setFileError('Only .xlsx ROTA workbooks are accepted.')
      setFile(null)
      return
    }
    if (next.size > MAX_BYTES) {
      setFileError('File exceeds the 10 MB limit.')
      setFile(null)
      return
    }
    setFileError(null)
    setFile(next)
  }

  function handleDrop(e: React.DragEvent): void {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const dropped = e.dataTransfer.files?.[0]
    if (dropped) acceptFile(dropped)
  }

  const canPreview = !!companyId && !!file && !!startDate && !!endDate && startDate <= endDate

  async function handlePreview(): Promise<void> {
    if (!file || !companyId) return
    setIssues([])
    try {
      const result = await previewMutation.mutateAsync({
        companyId,
        file,
        startDate,
        endDate,
        conflictPolicy,
      })
      setPreview(result)
    } catch (error) {
      const rotaIssues = extractRotaIssues(error)
      if (rotaIssues) {
        setIssues(rotaIssues)
        setPreview(null)
      } else {
        setFileError(getErrorMessage(error))
      }
    }
  }

  async function handleCommit(): Promise<void> {
    if (!file || !companyId || !confirmAck) return
    try {
      await commitMutation.mutateAsync({
        companyId,
        file,
        startDate,
        endDate,
        conflictPolicy,
      })
      onClose()
    } catch (error) {
      const rotaIssues = extractRotaIssues(error)
      if (rotaIssues) {
        setIssues(rotaIssues)
        setPreview(null)
      }
      // toast handled in the hook's onError
    }
  }

  const totals = preview?.totals

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload ROTA"
      className="max-w-3xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {preview ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleCommit}
              disabled={!confirmAck} loading={commitMutation.isPending}
            >
              {conflictPolicy === 'replace'
                ? 'Replace & create shifts'
                : 'Skip conflicts & create shifts'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handlePreview}
              disabled={!canPreview} loading={previewMutation.isPending}
            >
              Preview
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        {/* Company */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Company
          </label>
          {lockedCompany ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-100">
              {companies[0]?.name}
            </div>
          ) : (
            <Select
              options={[{ value: '', label: 'Select a company' }, ...companyOptions]}
              value={companyId}
              onChange={(value) => {
                setCompanyId(value)
                invalidatePreview()
              }}
            />
          )}
        </div>

        {/* Conflict policy */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Existing shifts
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                {
                  value: 'replace',
                  label: 'Replace existing shifts',
                  description:
                    'Remove matching scheduled or confirmed shifts for selected users and dates.',
                },
                {
                  value: 'skip',
                  label: 'Skip existing shifts',
                  description: 'Keep matching shifts and only create assignments for open dates.',
                },
              ] as const
            ).map((option) => {
              const selected = conflictPolicy === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setConflictPolicy(option.value)
                    invalidatePreview()
                  }}
                  className={cn(
                    'rounded-xl border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    selected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-800'
                  )}
                  aria-pressed={selected}
                >
                  <span className="block text-sm font-semibold">{option.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500 dark:text-gray-400">
                    {option.description}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Template guidance */}
        <div className="rounded-2xl border border-gray-200 p-4 text-sm dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-300">
            Upload an <span className="font-medium">.xlsx</span> matching the ROTA template: an
            employee-ID row, a Monday–Sunday header row, then a Time row with{' '}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">HH:mm - HH:mm</code> or{' '}
            <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">OFF</code> per day. The
            weekly pattern repeats across the date range.
          </p>
          <a
            href={TEMPLATE_HREF}
            download
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Download template
          </a>
        </div>

        {/* File dropzone */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            ROTA file
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={XLSX_EXT}
            onChange={(e) => acceptFile(e.target.files?.[0] ?? null)}
            className="sr-only"
            aria-label="Choose ROTA file"
          />
          {file ? (
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50">
              <span className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                <FileSpreadsheet className="h-4 w-4 text-primary" />
                {file.name}
              </span>
              <button
                type="button"
                onClick={() => acceptFile(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              onDragEnter={(e) => {
                e.preventDefault()
                if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => {
                e.preventDefault()
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
              }}
              onDrop={handleDrop}
              aria-label="Drop ROTA file here or click to browse"
              className={cn(
                'flex min-h-[120px] flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors',
                isDragging
                  ? 'border-primary bg-primary/10 dark:bg-primary/20'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800/50 dark:hover:border-gray-500'
              )}
            >
              <Upload
                className={cn('mb-2 h-8 w-8', isDragging ? 'text-primary' : 'text-gray-400')}
                aria-hidden
              />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Drag &amp; drop an .xlsx, or <span className="text-primary underline">browse</span>
              </p>
            </div>
          )}
          {fileError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fileError}</p>}
        </div>

        {/* Date range */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Start date
            </label>
            <DateInput
              value={startDate}
              max={endDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                invalidatePreview()
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              End date
            </label>
            <DateInput
              value={endDate}
              min={startDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                invalidatePreview()
              }}
            />
          </div>
        </div>

        {/* Validation report */}
        {issues.length > 0 && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4" />
              {issues.length} validation {issues.length === 1 ? 'issue' : 'issues'}
            </h3>
            <ul className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-300">
              {issues.map((issue, idx) => (
                <li key={idx}>
                  {issue.row ? `Row ${issue.row}` : issue.sheet}
                  {issue.column ? ` · ${issue.column}` : ''}
                  {issue.employeeId ? ` · ${issue.employeeId}` : ''}: {issue.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upload summary */}
        {preview && totals && (
          <div className="space-y-3 rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {conflictPolicy === 'replace' ? 'Replacement summary' : 'Skip summary'}
            </h3>
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <SummaryStat label="Employees" value={preview.employeeCount} />
              <SummaryStat label="Days" value={preview.dayCount} />
              <SummaryStat label="Shifts to create" value={totals.shiftsToCreate} />
              <SummaryStat
                label={conflictPolicy === 'replace' ? 'To replace' : 'To skip'}
                value={
                  conflictPolicy === 'replace'
                    ? totals.assignmentsToReplace
                    : totals.assignmentsToSkip
                }
              />
            </dl>
            {(preview.ineligibleShifts?.length ?? 0) > 0 && (
              <div
                data-testid="rota-ineligible-shifts"
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
              >
                <p>
                  <span className="font-medium">{preview.ineligibleShifts!.length}</span> shift(s)
                  will not be created because the employee can't work that day. The rest of the
                  ROTA is still uploaded.
                </p>
                <ul className="mt-2 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs">
                  {preview.ineligibleShifts!.map((item) => (
                    <li key={`${item.userId}:${item.date}`}>
                      {item.employeeId} · {item.date}: {describeEligibilityReasons(item.reasons)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {conflictPolicy === 'replace' ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
                <span className="font-medium">{totals.assignmentsToReplace}</span> existing
                scheduled/confirmed assignment(s) for these employees and dates will be permanently
                removed and replaced. Employees not in the file are untouched.
              </div>
            ) : (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-200">
                <span className="font-medium">{totals.assignmentsToSkip}</span> existing
                scheduled/confirmed assignment(s) will be kept. Matching imported shifts for those
                employee/date pairs will be skipped.
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={confirmAck}
                onChange={(e) => setConfirmAck(e.target.checked)}
                className="mt-0.5"
              />
              {conflictPolicy === 'replace'
                ? 'I understand this replacement is irreversible.'
                : 'I understand existing shifts will be kept and conflicting imported shifts will be skipped.'}
            </label>
          </div>
        )}
      </div>
    </Modal>
  )
}

function SummaryStat({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  )
}
